/**
 * Workspace utility functions.
 *
 * Workspace slugs are extracted from:
 * - Query param: dev-ui.lickly.com?ws=workspacenumber1
 * - URL path:    dev-ui.lickly.com/workspacenumber1
 * - localStorage fallback for local development
 */

/**
 * Extract workspace slug from URL query param or path.
 *
 * @returns {string|null} Workspace slug or null if not present
 */
export function getWorkspaceFromUrl() {
  // 1. Check query param: ?ws=workspaceslug
  const params = new URLSearchParams(window.location.search);
  const wsParam = params.get('ws');
  if (wsParam) {
    return wsParam;
  }

  // 2. Check URL path: /workspaceslug (first segment only, skip known routes)
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0];
    const knownRoutes = [
      'signup',
      'login',
      'forgot-password',
      'reset-password',
      'verify-email',
      'oauth',
      'callback',
      'campaigns',
      'persona',
      'ecommerce',
      'settings',
      'competitiveIntel',
      'mediaRanking',
      'workspace',
    ];
    if (!knownRoutes.includes(firstSegment)) {
      return firstSegment;
    }
  }

  // 3. Fallback: check localStorage for local development
  return localStorage.getItem('workspace_slug') || null;
}

/**
 * Set workspace slug in localStorage for local development.
 *
 * @param {string} workspaceSlug - Workspace slug to store
 */
export function setLocalWorkspace(workspaceSlug) {
  if (workspaceSlug) {
    localStorage.setItem('workspace_slug', workspaceSlug);
  } else {
    localStorage.removeItem('workspace_slug');
  }
}

/**
 * Build workspace URL with query param for a given workspace slug.
 *
 * @param {string} workspaceSlug - Workspace slug
 * @param {string} [path='/'] - Path within the workspace
 * @returns {string} Full workspace URL
 */
export function buildWorkspaceUrl(workspaceSlug, path = '/') {
  const { protocol, hostname, port } = window.location;
  const portStr = port ? `:${port}` : '';
  return `${protocol}//${hostname}${portStr}${path}?ws=${workspaceSlug}`;
}

/**
 * Redirect to workspace URL.
 *
 * @param {string} workspaceSlug - Workspace slug to redirect to
 * @param {string} [path='/'] - Path to redirect to
 */
export function redirectToWorkspace(workspaceSlug, path = '/') {
  window.location.href = buildWorkspaceUrl(workspaceSlug, path);
}

/**
 * Check if a workspace is present in the current URL.
 *
 * @returns {boolean} True if workspace is found
 */
export function hasWorkspaceInUrl() {
  return getWorkspaceFromUrl() !== null;
}

/**
 * Store pending workspace for after email verification.
 *
 * @param {string} workspaceSlug - Workspace slug to store
 */
export function setPendingWorkspace(workspaceSlug) {
  if (workspaceSlug) {
    localStorage.setItem('pending_workspace', workspaceSlug);
  }
}

/**
 * Get and clear pending workspace after email verification.
 *
 * @returns {string|null} Pending workspace slug or null
 */
export function getPendingWorkspace() {
  const workspace = localStorage.getItem('pending_workspace');
  localStorage.removeItem('pending_workspace');
  return workspace;
}
